import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useNotifications, useUnreadDMs, useUnreadCommunityReplies, useMyCommunityActivity, useUnreadWalletTips, useNotifPrefs, type Notification, type NotifType } from '../hooks/useNotifications';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import { useTipWallet, onStampSwept } from '../hooks/useTipWallet';
import { useFollows } from '../hooks/useFollows';
import { useViewportBottom } from '../hooks/useViewportBottom';
import { getStoredStamp } from '../lib/referral';
import { getBCHPrice } from '../hooks/useBCHPrice';
import Composer from './Composer';
import BCHTipButton from './BCHTipButton';
import IdentitySwitcher from './IdentitySwitcher';
import CreateBusinessModal from './CreateBusinessModal';
import { startScheduler, stopScheduler, getPendingPosts, onScheduleChange, onPostPublished, getScheduledPosts, cancelScheduledPost, reschedulePost, retryPost, formatScheduledTime, timeUntil, getSchedulePresets, type ScheduledPost } from '../lib/scheduledPosts';
import { useNostrPublish } from '../hooks/useNostrPublish';
// WizardConnect moved to Settings > Wallet
import {
   Home, Compass, Search, Settings, Radio, Activity,
   Store, Briefcase, MessageCircle, MessageSquarePlus, Award, Target,
   Mic2, Bell, Menu, X, Moon, Sun, LogIn, LogOut, User, Users, ChevronDown, Wallet, HelpCircle, Droplets, ArrowLeftRight, Bookmark, Info, ShoppingBag, BarChart3, ExternalLink, PenLine, Gift, CalendarClock, Clock, Trash2, CheckCircle, AlertTriangle, RotateCw, Image as ImageIcon, Music, Film, Crown, BadgeCheck, Heart, Zap, Repeat2, AtSign, CheckCheck, SlidersHorizontal, BellRing, Star, Coins,
   Play, Pause, SkipForward, Disc3, Loader2, Podcast, Video, Feather, Globe,
} from 'lucide-react';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';
import { formatDuration } from '../lib/radio';
import { isDataSaverEnabled, setDataSaver } from '../lib/platform';
import { recordNavVisit } from '../lib/navUsage';
import { useNavUsage } from '../hooks/useNavUsage';

// Pages where the floating compose button makes sense
const COMPOSE_PATHS = ['/', '/explore', '/search', '/notifications', '/bookmarks', '/leaderboard'];

type NavItem =
  | { path: string; label: string; icon: any; auth?: boolean; external?: boolean; divider?: never; sectionLabel?: never }
  | { divider: true; path?: never; label?: never; icon?: never; auth?: never; external?: never; sectionLabel?: string }

const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/explore', label: 'Explore', icon: Compass },
  { path: '/activities', label: 'Activities', icon: Activity },
  { path: '/search', label: 'Search', icon: Search },
  { path: '/notifications', label: 'Notifications', icon: Bell, auth: true },
  { path: '/messages', label: 'Messages', icon: MessageCircle, auth: true },
  { path: '/bookmarks', label: 'Bookmarks', icon: Bookmark, auth: true },

  { divider: true, sectionLabel: 'BCH Tools' },
  { path: '/settings?tab=wallet', label: 'Wallet', icon: Wallet, auth: true },
  { path: '/stamps', label: 'CashStamps', icon: Gift },
  { path: '/tips', label: 'Tip Manager', icon: Coins },
  { path: '/swap', label: 'Swap', icon: ArrowLeftRight },
  { path: '/tokens', label: 'BCH Tokens', icon: Coins },
  { path: '/faucet', label: 'Free BCH', icon: Droplets },

  { divider: true, sectionLabel: 'Community' },
  { path: '/communities', label: 'Community Spaces', icon: Users },
  { path: '/events', label: 'Events', icon: CalendarClock },
  { path: '/rooms', label: 'BCH Rooms', icon: Mic2 },
  { path: '/live', label: 'BCH Live', icon: Radio },
  { path: '/radio', label: 'BCH Radio', icon: Music },
  { path: '/podcasts', label: 'BCH Podcasts', icon: Podcast },
  { path: '/videos', label: 'BCH Video', icon: Video },
  { path: '/clips', label: 'Cash Clips', icon: Film },
  { path: '/games', label: 'BCH Games', icon: Crown },

  { divider: true, sectionLabel: 'Commerce' },
  { path: '/ecosystem', label: 'BCH Ecosystem', icon: Globe },
  { path: '/merchants', label: 'Merchants', icon: Store },
  { path: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
  { path: '/jobs', label: 'BCH Jobs', icon: Briefcase },

  { divider: true, sectionLabel: 'Engage' },
  { path: '/impact', label: 'My Impact', icon: Heart, auth: true },
  { path: '/leaderboard', label: 'Grow BCHNostr', icon: Award },
  { path: '/polls', label: 'Polls', icon: BarChart3 },
  { path: '/capsules', label: 'Time Capsules', icon: Clock },
  { path: '/support', label: 'Creator Support', icon: Heart },

  { divider: true, sectionLabel: 'Info & Help' },
  { path: '/ink', label: 'BCH Ink', icon: Feather },
  { path: '/feedback', label: 'Suggestion Box', icon: MessageSquarePlus },
  { path: '/about', label: 'About', icon: Info },
  { path: '/faq', label: 'FAQ', icon: HelpCircle },

  { divider: true, sectionLabel: '' },
  { path: '/settings', label: 'Settings', icon: Settings, auth: true },
];

// Fast lookup from path → nav item, for rendering the auto-populated
// "Quick Access" zone from tracked usage. Excludes dividers and external
// links (Quick Access only surfaces in-app destinations).
type NavLink = Extract<NavItem, { path: string }>;
const NAV_BY_PATH: Record<string, NavLink> = {};
for (const item of NAV_ITEMS) {
  if (!item.divider && !item.external) NAV_BY_PATH[item.path] = item;
}

export default function Layout() {
  const { user, login, logout, theme, toggleTheme } = useAppContext();
  const { price, change24h } = useBCHPrice();
  const { follows } = useFollows(user?.pubkey);
  const { notifications, unreadCount, markAllRead, lastReadTime } = useNotifications(user?.pubkey, follows);
  const { unreadDMs, markDMsRead } = useUnreadDMs(user?.pubkey);
  const { unreadReplies: unreadCommunityReplies, markCommunityRead } = useUnreadCommunityReplies(user?.pubkey);
  // Activity across the communities the user is a MEMBER of (new posts + chat),
  // not just direct replies. This drives the mobile Community Spaces bell badge.
  const { unreadCount: unreadCommunityActivity, markRead: markCommunityActivityRead } = useMyCommunityActivity(user?.pubkey);
  // The badge reflects whichever signal has unread items — group activity or a
  // direct reply to the user's own community post.
  const unreadCommunity = Math.max(unreadCommunityReplies, unreadCommunityActivity);
  const markAllCommunityRead = useCallback(() => {
    markCommunityRead();
    markCommunityActivityRead();
  }, [markCommunityRead, markCommunityActivityRead]);
  const { unreadTips, markWalletViewed } = useUnreadWalletTips(user?.pubkey);
  // Keeps bottom-fixed chrome welded to the visible edge on mobile, where
  // the layout and visual viewports disagree (collapsing URL bar, keyboard).
  const viewportBottom = useViewportBottom();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [dataSaver, setDataSaverState] = useState(isDataSaverEnabled);
  const [composeOpen, setComposeOpen] = useState(false);
  const [stampToast, setStampToast] = useState<{ amountSats: number; txid: string } | null>(null);
  const [showScheduledQueue, setShowScheduledQueue] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => getPendingPosts().length);
  const [publishToast, setPublishToast] = useState<{ content: string } | null>(null);
  const [showCreateBusiness, setShowCreateBusiness] = useState(false);
  const { publish } = useNostrPublish();
  const location = useLocation();

  const showFAB = user && COMPOSE_PATHS.includes(location.pathname);

  // ── Start the scheduled posts background publisher ──
  useEffect(() => {
    if (!user) return;
    startScheduler(publish);
    return () => stopScheduler();
  }, [user, publish]);

  // ── Track pending scheduled post count for badge ──
  useEffect(() => {
    const update = () => setPendingCount(getPendingPosts().length);
    update();
    return onScheduleChange(update);
  }, []);

  // ── Toast when a scheduled post is published ──
  useEffect(() => {
    return onPostPublished((post) => {
      setPublishToast({ content: post.content.slice(0, 80) || '(media post)' });
      setTimeout(() => setPublishToast(null), 5000);
    });
  }, []);

  // ── After a business sign-up, open the Create Business Page modal ──
  // The Hero "Create a business account" button stores this intent in
  // localStorage, then opens the sign-up dialog. Logging in triggers a full
  // page reload (useCurrentUser.finishLogin), so the flag has to be in
  // localStorage to survive it. Once the user has a PERSONAL account, we open
  // the business-creation flow (a Business Page must be owned by a personal
  // account, so we can't create one from a business identity).
  useEffect(() => {
    if (!user || user.isBusiness) return;
    let wants = false;
    try { wants = localStorage.getItem('bchnostr:after-signup-business') === '1'; } catch { /* ignore */ }
    if (wants) {
      try { localStorage.removeItem('bchnostr:after-signup-business'); } catch { /* ignore */ }
      setShowCreateBusiness(true);
    }
  }, [user]);

  // ── Eagerly initialize the tip wallet when a stamp is pending ──
  // On mobile the sidebar (and WalletBalanceCard) doesn't render until
  // the user opens the hamburger menu. If there's a pending stamp to
  // sweep, we need to init the wallet immediately so the sweep happens
  // without requiring a sidebar open.
  const stampInitRef = useRef(false);
  const tipWalletForSweep = useTipWallet();
  useEffect(() => {
    if (!user || stampInitRef.current) return;
    const pendingStamp = getStoredStamp();
    if (pendingStamp) {
      stampInitRef.current = true;
      tipWalletForSweep.init().catch(() => {});
    }
  }, [user]);

  // Listen for CashStamp sweep completion
  useEffect(() => {
    return onStampSwept((result) => {
      setStampToast(result);
      // Auto-dismiss after 8 seconds
      setTimeout(() => setStampToast(null), 8000);
    });
  }, []);

  return (
    <>
    <div className="min-h-screen min-h-[100dvh] bg-background text-foreground">
      {/* CashStamp success toast */}
      {stampToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-slide-down">
          <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-emerald-500 text-white shadow-2xl shadow-emerald-500/30 border border-emerald-400/30">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <BCHLogo size={24} />
            </div>
            <div>
              <p className="text-sm font-bold">BCH Received!</p>
              <p className="text-xs text-white/80">
                {(() => {
                  const bchPrice = getBCHPrice();
                  const bch = stampToast.amountSats / 1e8;
                  const usd = bchPrice ? (bch * bchPrice).toFixed(2) : null;
                  return usd
                    ? `$${usd} (${stampToast.amountSats.toLocaleString()} sats) added to your wallet`
                    : `${stampToast.amountSats.toLocaleString()} sats added to your wallet`;
                })()}
              </p>
            </div>
            <button
              onClick={() => setStampToast(null)}
              className="p-1 rounded-lg hover:bg-white/20 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Scheduled post published toast */}
      {publishToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-slide-down">
          <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-primary text-primary-foreground shadow-2xl shadow-primary/30 border border-primary/30">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold">Scheduled Post Published!</p>
              <p className="text-xs opacity-80 line-clamp-1 max-w-[200px]">{publishToast.content}</p>
            </div>
            <button
              onClick={() => setPublishToast(null)}
              className="p-1 rounded-lg hover:bg-white/20 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-4 py-3 border-b border-border bg-background/80 backdrop-blur-md lg:hidden safe-area-top">
        <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-muted">
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <img src="/icons/icon-512x512.png" alt="BCHNostr" className="w-7 h-7 rounded-md" />
          <span className="text-lg font-bold text-bch-gradient">BCHNostr</span>
          {price && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs font-bold">
              <BCHLogo size={11} />${price.toFixed(2)}
            </span>
          )}
        </Link>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative p-1.5 rounded-lg hover:bg-muted"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              const next = !dataSaver;
              setDataSaver(next);
              setDataSaverState(next);
            }}
            className={`p-1.5 rounded-lg transition-colors ${dataSaver ? 'text-emerald-500 bg-emerald-500/10' : 'hover:bg-muted text-muted-foreground'}`}
            title={dataSaver ? 'Data Saver: ON — tap to disable' : 'Data Saver: OFF — tap to reduce data usage'}
          >
            <Zap className="w-5 h-5" />
          </button>
          <button onClick={toggleTheme} className="p-1.5 rounded-lg hover:bg-muted">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Notification Panel */}
      {notifOpen && (
        <NotificationPanel
          notifications={notifications}
          lastReadTime={lastReadTime}
          markAllRead={markAllRead}
          onClose={() => setNotifOpen(false)}
          unreadDMs={unreadDMs}
        />
      )}

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="w-72 h-full bg-card border-r border-border overflow-y-auto safe-area-top safe-area-bottom" onClick={e => e.stopPropagation()}>
             <SidebarContent
              user={user}
              login={login}
              logout={logout}
              location={location}
              price={price}
              change24h={change24h}
              theme={theme}
              toggleTheme={toggleTheme}
              onClose={() => setSidebarOpen(false)}
              unreadCount={unreadCount}
              onBellClick={() => { setSidebarOpen(false); setNotifOpen(!notifOpen); }}
              unreadDMs={unreadDMs}
              markDMsRead={markDMsRead}
              unreadCommunity={unreadCommunity}
              markCommunityRead={markAllCommunityRead}
              unreadTips={unreadTips}
              markWalletViewed={markWalletViewed}
            />
          </div>
        </div>
      )}

      <div className="flex max-w-7xl mx-auto">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 xl:w-72 shrink-0 sticky top-0 h-screen overflow-y-auto border-r border-border">
           <SidebarContent
            user={user}
            login={login}
            logout={logout}
            location={location}
            price={price}
            change24h={change24h}
            theme={theme}
            toggleTheme={toggleTheme}
            unreadCount={unreadCount}
            onBellClick={() => setNotifOpen(!notifOpen)}
            unreadDMs={unreadDMs}
            markDMsRead={markDMsRead}
            unreadCommunity={unreadCommunity}
            markCommunityRead={markAllCommunityRead}
            unreadTips={unreadTips}
            markWalletViewed={markWalletViewed}
          />
        </aside>

        {/* Main Content — extra bottom padding on mobile for bottom nav + iOS safe area */}
        <main className="flex-1 min-w-0 lg:pb-8" style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}>
          <Outlet />
        </main>
      </div>

    </div>

      {/* Floating Compose Button — outside wrapper for reliable fixed positioning */}
      {showFAB && (
        <div
          className="fixed right-4 z-40 lg:bottom-8 lg:right-8 flex flex-col items-end gap-2"
          style={{
            bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
            // Ride with the nav so the FAB never detaches from it.
            transform: viewportBottom ? `translateY(-${viewportBottom}px)` : undefined,
          }}
        >
          {/* Scheduled posts indicator */}
          {pendingCount > 0 && (
            <button
              onClick={() => setShowScheduledQueue(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border shadow-lg text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <CalendarClock className="w-3.5 h-3.5 text-primary" />
              {pendingCount} scheduled
            </button>
          )}
          <button
            onClick={() => setComposeOpen(true)}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 hover:shadow-xl hover:shadow-primary/40 active:scale-95 transition-all"
            aria-label="Write a post"
          >
            <PenLine className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Scheduled Posts Queue Modal */}
      {showScheduledQueue && (
        <ScheduledQueueModal onClose={() => setShowScheduledQueue(false)} />
      )}

      {/* Compose Modal */}
      {composeOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setComposeOpen(false)}
        >
          <div
            className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <span className="text-sm font-semibold">New Post</span>
              <button onClick={() => setComposeOpen(false)} className="p-1.5 rounded-lg hover:bg-muted">
                <X className="w-4 h-4" />
              </button>
            </div>
            <Composer
              onPublished={() => setComposeOpen(false)}
              placeholder="What's on your mind?"
            />
          </div>
        </div>
      )}

      {/* Persistent Radio Mini-Player — shown when audio is playing/paused */}
      <RadioMiniPlayer />
      <RadioMiniPlayerDesktop />

      {/* Mobile Bottom Nav — rendered OUTSIDE the layout wrapper to prevent
          fixed positioning issues caused by parent transforms/backdrop-filter.
          `bottom: 0` alone pins to the LAYOUT viewport, which on iOS Safari
          stays full-height while the collapsing URL bar shrinks the VISUAL
          viewport — that mismatch is what made the bar drift while scrolling.
          Translating by the gap keeps it on the visible edge. */}
      <nav
        data-mobile-nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-card/90 backdrop-blur-md border-t border-border lg:hidden safe-area-bottom"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, width: '100%', zIndex: 40,
          transform: viewportBottom ? `translateY(-${viewportBottom}px)` : undefined,
        }}
      >
        <div className="flex items-center justify-around py-2">
          {[
            { path: '/', icon: Home, label: 'Home', badge: 0 },
            { path: '/communities', icon: Users, label: 'Spaces', badge: unreadCommunity },
            { path: '/settings?tab=wallet', icon: Wallet, label: 'Wallet', badge: unreadTips },
            { path: '/clips', icon: Film, label: 'Clips', badge: 0 },
            { path: '/settings', icon: Menu, label: 'More', badge: 0 },
          ].map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                if (item.path === '/messages') markDMsRead();
                if (item.path === '/notifications') markAllRead();
                if (item.path === '/communities') markAllCommunityRead();
                if (item.path === '/settings?tab=wallet') markWalletViewed();
                recordNavVisit(item.path);
              }}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs transition-colors relative ${
                (item.path.includes('?')
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path)
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="relative">
                <item.icon className="w-5 h-5" />
                {item.badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Create Business Page — opened automatically after a business sign-up
          (see the __bchnostr_after_signup_business effect above). */}
      {showCreateBusiness && (
        <CreateBusinessModal autoSwitch onClose={() => setShowCreateBusiness(false)} />
      )}
    </>
  );
}

const WALLET_BACKUP_DISMISSED_KEY = 'bchnostr:wallet-backup-dismissed';

function WalletBalanceCard() {
  const tipWallet = useTipWallet();
  const [initializing, setInitializing] = useState(true);
  const initCalledRef = useRef(false);
  const [backupDismissed, setBackupDismissed] = useState(() => {
    try { return localStorage.getItem(WALLET_BACKUP_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;
    tipWallet.init().catch(() => {}).finally(() => setInitializing(false));
  }, []);

  const satsBalance = Number(tipWallet.balanceSats);
  const displayBalance = `${satsBalance.toLocaleString()} sats`;
  const hasBalance = satsBalance > 0;

  // Show spinner while wallet is initializing or loading, but only if balance is still 0
  const showSpinner = (initializing || tipWallet.loading) && tipWallet.balanceSats === BigInt(0);

  // Show backup warning if balance > 0 and user hasn't dismissed it
  const showBackupWarning = hasBalance && !backupDismissed;

  return (
    <div className="border-t border-border/60 space-y-0">
      <Link to="/settings?tab=wallet" className="flex items-center gap-2 px-4 py-2.5 hover:bg-muted/50 transition-colors">
        <Wallet className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-semibold flex-1">Wallet</span>
        {showSpinner ? (
          <div className="w-3.5 h-3.5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        ) : (
          <span className="text-sm font-mono font-bold text-primary">{displayBalance}</span>
        )}
      </Link>
      {showBackupWarning && (
        <div className="flex items-start gap-2 mx-4 mb-2 rounded-xl bg-amber-500/10 border border-amber-500/20 px-3 py-2 relative">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">Back up your wallet!</p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Your BCH is stored in this browser. If you clear data, it's gone.{' '}
              <Link to="/settings?tab=wallet" className="text-primary hover:underline font-medium">Back up now →</Link>
            </p>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              setBackupDismissed(true);
              try { localStorage.setItem(WALLET_BACKUP_DISMISSED_KEY, '1'); } catch {}
            }}
            className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ user, login, logout, location, price, change24h, theme, toggleTheme, onClose, unreadCount, onBellClick, unreadDMs, markDMsRead, unreadCommunity, markCommunityRead, unreadTips, markWalletViewed }: any) {
  // Auto-populated "Quick Access" shortcuts, derived from usage. The main nav
  // list below stays fixed — this only adds a small learned zone at the top.
  const topPaths = useNavUsage(5);
  const quickItems = topPaths
    .map(p => NAV_BY_PATH[p])
    .filter((it): it is NavLink => !!it && (!it.auth || !!user));

  return (
    <div className="flex flex-col h-full">

      {/* ── Top: Logo row ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <Link to="/" className="flex items-center gap-3" onClick={onClose}>
          <img src="/icons/icon-512x512.png" alt="BCHNostr" className="w-10 h-10 rounded-xl" />
          <span className="text-2xl font-black tracking-tight text-bch-gradient">BCHNostr</span>
        </Link>
        <div className="flex items-center gap-1">
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-muted transition-colors lg:hidden">
              <X className="w-5 h-5" />
            </button>
          )}
          <button onClick={toggleTheme} className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* ── User Profile Card ──────────────────────────────────────── */}
      {user ? (
        <div className="mx-4 mb-4 rounded-2xl bg-muted/50 border border-border overflow-hidden">
          <Link to={`/profile/${user.pubkey}`} className="flex items-center gap-3 p-4" onClick={onClose}>
            {user.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover shrink-0 ring-2 ring-primary/20" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 text-primary" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-bold text-base truncate">{user.name || 'Anonymous'}</p>
                {user.isBusiness && (
                  <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">
                    Business
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate font-mono">{user.pubkey.slice(0, 16)}…</p>
            </div>
          </Link>
          {/* Wallet strip */}
          <WalletBalanceCard />
        </div>
      ) : (
        <div className="mx-4 mb-4 space-y-2">
          <button
            onClick={login}
            className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 transition-colors"
          >
            <LogIn className="w-5 h-5" /> Log In
          </button>
          <button
            onClick={() => { (window as any).__bchnostr_signup = true; login(); }}
            className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border-2 border-primary text-primary font-bold text-base hover:bg-primary/5 transition-colors"
          >
            <User className="w-5 h-5" /> Sign Up
          </button>
        </div>
      )}

      {/* ── Identity switcher (personal ⇄ business Pages) ───────────── */}
      {user && <IdentitySwitcher onNavigate={onClose} />}

      {/* ── Navigation ──────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
        {/* Profile */}
        {user && (
          <Link
            to={`/profile/${user.pubkey}`}
            onClick={onClose}
            className={`flex items-center gap-4 px-3 py-3.5 rounded-2xl font-semibold text-base transition-all ${
              location.pathname.startsWith('/profile/')
                ? 'bg-primary/10 text-primary'
                : 'text-foreground/80 hover:bg-muted hover:text-foreground'
            }`}
          >
            <User className="w-6 h-6 shrink-0" />
            My Profile
          </Link>
        )}

        {/* ── Quick Access — auto-populated from usage (fixed list unchanged) ── */}
        {quickItems.length >= 2 && (
          <>
            <div className="pt-4 pb-1.5 px-3 flex items-center gap-1.5">
              <Star className="w-3 h-3 text-primary/60" />
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/40">
                Quick Access
              </p>
            </div>
            {quickItems.map(item => (
              <Link
                key={`quick-${item.path}`}
                to={item.path}
                onClick={() => {
                  if (item.path === '/messages') markDMsRead?.();
                  if (item.path === '/communities') markCommunityRead?.();
                  if (item.path === '/settings?tab=wallet') markWalletViewed?.();
                  recordNavVisit(item.path);
                  onClose?.();
                }}
                className={`flex items-center gap-4 px-3 py-3.5 rounded-2xl font-semibold text-base transition-all ${
                  (item.path.includes('?')
                    ? location.pathname + location.search === item.path
                    : location.pathname === item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon className="w-6 h-6 shrink-0" />
                <span className="flex-1">{item.label}</span>
              </Link>
            ))}
          </>
        )}

        {NAV_ITEMS.filter(item => !item.auth || user).map((item, idx) => (
          item.divider ? (
            <div key={`div-${idx}`} className="pt-4 pb-1.5 px-3">
              {item.sectionLabel && (
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/40">
                  {item.sectionLabel}
                </p>
              )}
            </div>
          ) : item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center gap-4 px-3 py-3.5 rounded-2xl font-semibold text-base transition-all text-foreground/80 hover:bg-muted hover:text-foreground"
            >
              <item.icon className="w-6 h-6 shrink-0" />
              <span className="flex-1">{item.label}</span>
              <ExternalLink className="w-4 h-4 opacity-30 shrink-0" />
            </a>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                if (item.path === '/messages') markDMsRead?.();
                if (item.path === '/communities') markCommunityRead?.();
                if (item.path === '/settings?tab=wallet') markWalletViewed?.();
                recordNavVisit(item.path!);
                onClose?.();
              }}
              className={`flex items-center gap-4 px-3 py-3.5 rounded-2xl font-semibold text-base transition-all ${
                (item.path!.includes('?')
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path)
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:bg-muted hover:text-foreground'
              }`}
            >
              <div className="relative shrink-0">
                <item.icon className="w-6 h-6" />
                {item.path === '/messages' && unreadDMs > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {unreadDMs > 9 ? '9+' : unreadDMs}
                  </span>
                )}
                {item.path === '/communities' && unreadCommunity > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {unreadCommunity > 9 ? '9+' : unreadCommunity}
                  </span>
                )}
                {item.path === '/settings?tab=wallet' && unreadTips > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {unreadTips > 9 ? '9+' : unreadTips}
                  </span>
                )}
              </div>
              <span className="flex-1">{item.label}</span>
              {item.path === '/notifications' && unreadCount > 0 && (
                <span className="min-w-[1.25rem] h-5 px-1.5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold shrink-0">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          )
        ))}
      </nav>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <div className="px-4 pb-5 pt-3 border-t border-border space-y-3">
        {/* BCH price pill */}
        {price && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50">
            <BCHLogo size={16} />
            <span className="text-sm font-bold">${price.toFixed(2)}</span>
            <span className={`ml-auto text-xs font-semibold ${change24h >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {change24h >= 0 ? '+' : ''}{change24h.toFixed(1)}%
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          {user && (
            <button onClick={() => { logout(); window.location.href = '/'; }} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-500 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          )}
          <a
            href="bitcoincash:qzfczm3kc9j8aw0pjm5azym8s8xg07ssru5yr5qepj"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors ml-auto"
          >
            <BCHLogo size={13} />
            Support BCHNostr
          </a>
        </div>
        <p className="text-[10px] text-muted-foreground/50 text-center">
          Vibed with{' '}
          <a href="https://shakespeare.diy" target="_blank" rel="noopener" className="underline hover:text-muted-foreground">Shakespeare</a>
        </p>
      </div>
    </div>
  );
}

// ── Notification Panel (bell dropdown) ─────────────────────────────
type NotifFilter = 'all' | 'reply' | 'reaction' | 'zap' | 'mention' | 'repost' | 'dm' | 'friend_post' | 'favorite_post';
type PanelView = 'feed' | 'manage';

const NOTIF_TABS: { id: NotifFilter; label: string; icon: React.ReactNode; activeClass: string; badgeClass: string; inactiveBadge: string }[] = [
  { id: 'all',           label: 'All',       icon: <Bell className="w-3 h-3" />,           activeClass: 'bg-primary text-primary-foreground',  badgeClass: 'bg-primary-foreground/20 text-primary-foreground', inactiveBadge: 'bg-primary/10 text-primary' },
  { id: 'favorite_post', label: 'Favorites', icon: <Star className="w-3 h-3" />,           activeClass: 'bg-amber-500 text-white',             badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { id: 'friend_post',   label: 'Following', icon: <Users className="w-3 h-3" />,          activeClass: 'bg-sky-500 text-white',               badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  { id: 'reply',       label: 'Replies',  icon: <MessageCircle className="w-3 h-3" />,  activeClass: 'bg-amber-500 text-white',             badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { id: 'reaction',    label: 'Likes',    icon: <Heart className="w-3 h-3" />,          activeClass: 'bg-red-500 text-white',               badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-red-500/10 text-red-500' },
  { id: 'zap',         label: 'Zaps',     icon: <Zap className="w-3 h-3" />,            activeClass: 'bg-emerald-500 text-white',            badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  { id: 'dm',          label: 'Messages', icon: <MessageCircle className="w-3 h-3" />,  activeClass: 'bg-cyan-500 text-white',              badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
  { id: 'mention',     label: 'Mentions', icon: <AtSign className="w-3 h-3" />,         activeClass: 'bg-blue-500 text-white',              badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-blue-500/10 text-blue-500' },
  { id: 'repost',      label: 'Reposts',  icon: <Repeat2 className="w-3 h-3" />,        activeClass: 'bg-purple-500 text-white',             badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-purple-500/10 text-purple-500' },
];

const PANEL_TOGGLE_ITEMS: { type: NotifType; label: string; desc: string; icon: React.ReactNode; color: string; bg: string }[] = [
  { type: 'reply',         label: 'Replies',     desc: 'Replies to your posts',        icon: <MessageCircle className="w-3.5 h-3.5" />, color: 'text-amber-500',   bg: 'bg-amber-500/10' },
  { type: 'reaction',      label: 'Likes',       desc: 'Reactions to your posts',      icon: <Heart className="w-3.5 h-3.5" />,          color: 'text-red-500',     bg: 'bg-red-500/10' },
  { type: 'zap',           label: 'Zaps / Tips', desc: 'BCH tips you receive',         icon: <Zap className="w-3.5 h-3.5" />,            color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  { type: 'mention',       label: 'Mentions',    desc: 'When you\'re mentioned',       icon: <AtSign className="w-3.5 h-3.5" />,         color: 'text-blue-500',    bg: 'bg-blue-500/10' },
  { type: 'repost',        label: 'Reposts',     desc: 'Reposts of your content',      icon: <Repeat2 className="w-3.5 h-3.5" />,        color: 'text-purple-500',  bg: 'bg-purple-500/10' },
  { type: 'friend_post',   label: 'Following',   desc: 'Posts from people you follow', icon: <Users className="w-3.5 h-3.5" />,          color: 'text-sky-500',     bg: 'bg-sky-500/10' },
  { type: 'favorite_post', label: 'Favorites',   desc: 'Posts from starred users',     icon: <Star className="w-3.5 h-3.5" />,           color: 'text-amber-500',   bg: 'bg-amber-500/10' },
];

function NotificationPanel({ notifications, lastReadTime, markAllRead, onClose, unreadDMs }: {
  notifications: Notification[];
  lastReadTime: number;
  markAllRead: () => void;
  onClose: () => void;
  unreadDMs?: number;
}) {
  const navigate = useNavigate();
  const { prefs, togglePref } = useNotifPrefs();
  // Capture read time at open so unread highlights persist while viewing
  const [viewReadTime] = useState(() => lastReadTime);
  const [filter, setFilter] = useState<NotifFilter>('all');
  const [panelView, setPanelView] = useState<PanelView>('feed');

  // Auto-mark all read after a short delay
  useEffect(() => {
    const unread = notifications.filter(n => n.timestamp > viewReadTime).length;
    if (unread === 0) return;
    const timer = setTimeout(() => markAllRead(), 2000);
    return () => clearTimeout(timer);
  }, [notifications, viewReadTime, markAllRead]);

  // Count per type
  const typeCounts = useMemo(() => {
    const counts: Record<string, { total: number; unread: number }> = {};
    for (const tab of NOTIF_TABS) counts[tab.id] = { total: 0, unread: 0 };
    for (const n of notifications) {
      counts['all'].total++;
      if (n.timestamp > viewReadTime) counts['all'].unread++;
      if (counts[n.type]) {
        counts[n.type].total++;
        if (n.timestamp > viewReadTime) counts[n.type].unread++;
      }
    }
    // DM tab always shows with unread count from the DM hook
    const dmCount = unreadDMs || 0;
    counts['dm'] = { total: Math.max(1, dmCount), unread: dmCount };
    return counts;
  }, [notifications, viewReadTime, unreadDMs]);

  // Filter and split — respect prefs on "All" tab
  const filtered = useMemo(() => {
    if (filter === 'all') return notifications.filter(n => prefs[n.type] !== false);
    return notifications.filter(n => n.type === filter);
  }, [notifications, filter, prefs]);
  const unread = filtered.filter(n => n.timestamp > viewReadTime);
  const read = filtered.filter(n => n.timestamp <= viewReadTime);

  const enabledCount = PANEL_TOGGLE_ITEMS.filter(item => prefs[item.type] !== false).length;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        className="absolute right-0 top-14 lg:top-0 lg:right-4 lg:top-4 w-full max-w-sm bg-card border border-border rounded-b-2xl lg:rounded-2xl shadow-xl max-h-[70vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with tabs */}
        <div className="border-b border-border bg-muted/30">
          <div className="flex items-center justify-between px-4 py-2.5">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" /> Notifications
              {typeCounts['all'].unread > 0 && (
                <span className="text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {typeCounts['all'].unread}
                </span>
              )}
            </h3>
            <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Notifications / Manage toggle */}
          <div className="flex border-t border-border">
            <button
              onClick={() => setPanelView('feed')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                panelView === 'feed'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <BellRing className="w-3.5 h-3.5" />
              Notifications
            </button>
            <button
              onClick={() => setPanelView('manage')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                panelView === 'manage'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Manage
            </button>
          </div>

          {/* Filter tabs — only on feed view */}
          {panelView === 'feed' && (notifications.length > 0 || (unreadDMs || 0) > 0) && (
            <div className="flex gap-1 overflow-x-auto scrollbar-none px-3 py-2">
              {NOTIF_TABS.map(tab => {
                const counts = typeCounts[tab.id];
                if (tab.id !== 'all' && tab.id !== 'dm' && counts.total === 0) return null;
                const isActive = filter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (tab.id === 'dm') {
                        onClose();
                        navigate('/messages');
                      } else {
                        setFilter(tab.id);
                      }
                    }}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? tab.activeClass
                        : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    {counts.unread > 0 && (
                      <span className={`text-[9px] font-bold px-1 py-0 rounded-full tabular-nums ${
                        isActive ? tab.badgeClass : tab.inactiveBadge
                      }`}>
                        {counts.unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {panelView === 'manage' ? (
            /* ── Manage view: toggle list ─────────────────────── */
            <div className="px-3 py-3 space-y-1.5">
              <p className="text-[11px] text-muted-foreground px-1 mb-2">
                Toggle what shows in your feed and bell badge. Disabled types are hidden from "All" but still available via their tab.
              </p>
              {PANEL_TOGGLE_ITEMS.map(item => {
                const enabled = prefs[item.type] !== false;
                return (
                  <button
                    key={item.type}
                    onClick={() => togglePref(item.type)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all ${
                      enabled
                        ? 'bg-background border border-border'
                        : 'bg-muted/40 border border-transparent opacity-60'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                      enabled ? item.bg + ' ' + item.color : 'bg-muted text-muted-foreground'
                    }`}>
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-semibold leading-tight">{item.label}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{item.desc}</p>
                    </div>
                    <div className={`w-9 h-5 rounded-full flex items-center transition-colors shrink-0 ${
                      enabled ? 'bg-primary justify-end' : 'bg-muted-foreground/20 justify-start'
                    }`}>
                      <div className="w-4 h-4 rounded-full bg-white shadow-sm mx-0.5" />
                    </div>
                  </button>
                );
              })}
              <p className="text-[10px] text-muted-foreground text-center pt-1">
                {enabledCount} of {PANEL_TOGGLE_ITEMS.length} enabled · Saved automatically
              </p>
            </div>
          ) : (
            /* ── Feed view: notification list ─────────────────── */
            <>
              {notifications.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Bell className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
                  <p className="text-sm text-muted-foreground">No notifications yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Reactions, replies, and reposts will show up here.</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-sm text-muted-foreground">No {NOTIF_TABS.find(t => t.id === filter)?.label.toLowerCase()}</p>
                </div>
              ) : (
                <>
                  {/* Mark all read button */}
                  {unread.length > 0 && (
                    <div className="flex justify-end px-4 pt-2">
                      <button
                        onClick={markAllRead}
                        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                      >
                        <CheckCheck className="w-3 h-3" /> Mark all read
                      </button>
                    </div>
                  )}
                  {unread.map(notif => (
                    <NotificationItem key={notif.id} notif={notif} isUnread onClose={onClose} />
                  ))}
                  {unread.length > 0 && read.length > 0 && (
                    <div className="flex items-center gap-3 px-4 py-1.5 bg-muted/30">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Earlier</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                  {read.map(notif => (
                    <NotificationItem key={notif.id} notif={notif} isUnread={false} onClose={onClose} />
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer link to full page */}
        <Link
          to="/notifications"
          onClick={onClose}
          className="block text-center py-2.5 border-t border-border text-xs font-medium text-primary hover:bg-muted/30 transition-colors"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}

// Types of notifications where the user can like/tip the event content
const PANEL_ACTIONABLE_TYPES = new Set(['reply', 'mention', 'friend_post', 'favorite_post']);

function NotificationItem({ notif, isUnread, onClose }: { notif: Notification; isUnread: boolean; onClose: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(notif.fromPubkey);
  const name = useDisplayName(notif.fromPubkey, profile, notif.fromPubkey.slice(0, 8) + '...');
  const [liked, setLiked] = useState(false);

  const icons: Record<string, { emoji: string; label: string; color: string }> = {
    reply: { emoji: '\u{1F4AC}', label: 'replied to you', color: 'text-blue-500' },
    reaction: { emoji: '❤\uFE0F', label: 'liked your post', color: 'text-red-500' },
    repost: { emoji: '\u{1F504}', label: 'reposted your post', color: 'text-emerald-500' },
    zap: { emoji: '\u26A1', label: 'zapped you', color: 'text-amber-500' },
    mention: { emoji: '\u{1F4E2}', label: 'mentioned you', color: 'text-purple-500' },
    friend_post: { emoji: '\u{1F4DD}', label: 'posted', color: 'text-sky-500' },
    favorite_post: { emoji: '⭐', label: 'posted', color: 'text-amber-500' },
  };
  const info = icons[notif.type] || icons.mention;

  const timeAgoStr = (() => {
    const secs = Math.floor(Date.now() / 1000 - notif.timestamp);
    if (secs < 60) return 'just now';
    if (secs < 3600) return `${Math.floor(secs / 60)}m`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    return `${Math.floor(secs / 86400)}d`;
  })();

  const noteLinkTo = notif.type === 'reply' || notif.type === 'mention' || notif.type === 'friend_post' || notif.type === 'favorite_post'
    ? `/note/${notif.event.id}`
    : notif.relatedEventId
      ? `/note/${notif.relatedEventId}`
      : null;

  const canAct = PANEL_ACTIONABLE_TYPES.has(notif.type) && user;

  const handleLike = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || liked) return;
    setLiked(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [['e', notif.event.id], ['p', notif.fromPubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {
      setLiked(false);
    }
  }, [user, liked, publish, notif.event.id, notif.fromPubkey]);

  return (
    <div className={`flex items-start gap-2.5 px-4 py-2.5 transition-colors border-b border-border last:border-0 ${
      isUnread
        ? 'bg-primary/[0.06] border-l-[3px] border-l-primary'
        : 'hover:bg-muted/30 opacity-70'
    }`}>
      {/* Unread dot */}
      <div className="flex flex-col items-center pt-1.5 shrink-0 w-2.5">
        {isUnread && (
          <div className="w-2 h-2 rounded-full bg-primary shadow-sm shadow-primary/30 animate-pulse" />
        )}
      </div>

      {/* Avatar */}
      <Link to={`/profile/${notif.fromPubkey}`} onClick={onClose} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className={`w-8 h-8 rounded-full object-cover transition-all ${
            isUnread ? 'ring-2 ring-primary/30' : 'hover:ring-2 hover:ring-primary/40'
          }`} />
        ) : (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${
            isUnread ? 'bg-primary/15 ring-2 ring-primary/30' : 'bg-primary/10 hover:ring-2 hover:ring-primary/40'
          }`}>
            {info.emoji}
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <Link
          to={noteLinkTo ?? `/profile/${notif.fromPubkey}`}
          onClick={onClose}
        >
          <p className={`text-xs leading-snug ${isUnread ? '' : 'text-muted-foreground'}`}>
            <span className={`font-semibold hover:underline ${isUnread ? 'text-foreground' : 'text-foreground/70'}`}>{name}</span>{' '}
            <span className={isUnread ? info.color + ' font-medium' : 'text-muted-foreground'}>{info.label}</span>
          </p>
          {(notif.type === 'reply' || notif.type === 'friend_post' || notif.type === 'favorite_post') && notif.event.content && (
            <p className={`text-[11px] mt-0.5 line-clamp-2 ${isUnread ? 'text-foreground/70' : 'text-muted-foreground/60'}`}>
              {notif.event.content.slice(0, 120)}
            </p>
          )}
          {notif.type === 'reaction' && notif.event.content && notif.event.content !== '+' && (
            <p className="text-sm mt-0.5">{notif.event.content}</p>
          )}
        </Link>

        {/* Action row: time + like + tip */}
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] ${isUnread ? 'text-primary/70 font-medium' : 'text-muted-foreground'}`}>{timeAgoStr}</span>
          {canAct && (
            <>
              <span className="text-border text-[10px]">·</span>
              <button
                onClick={handleLike}
                disabled={liked}
                className={`flex items-center gap-0.5 text-[10px] font-medium transition-colors ${
                  liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                }`}
              >
                <Heart className={`w-3 h-3 ${liked ? 'fill-current' : ''}`} />
                {liked ? 'Liked' : 'Like'}
              </button>
              <span className="text-border text-[10px]">·</span>
              <span onClick={e => e.stopPropagation()}>
                <BCHTipButton
                  recipientPubkey={notif.fromPubkey}
                  recipientProfile={profile}
                  eventId={notif.event.id}
                />
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Scheduled Posts Queue Modal ─────────────────────────────────
function ScheduledQueueModal({ onClose }: { onClose: () => void }) {
  const [posts, setPosts] = useState<ScheduledPost[]>(() => getScheduledPosts());
  const [, setTick] = useState(0);

  // Refresh on changes and every 15s for countdown updates
  useEffect(() => {
    const unsub = onScheduleChange(() => setPosts(getScheduledPosts()));
    const interval = setInterval(() => {
      setPosts(getScheduledPosts());
      setTick(t => t + 1);
    }, 15_000);
    return () => { unsub(); clearInterval(interval); };
  }, []);

  const pending = posts.filter(p => p.status === 'pending');
  const failed = posts.filter(p => p.status === 'failed');
  const published = posts.filter(p => p.status === 'published');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-border">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" /> Scheduled Posts
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {posts.length === 0 ? (
            <div className="text-center py-12 px-4">
              <CalendarClock className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No scheduled posts</p>
              <p className="text-xs text-muted-foreground mt-1">Use the clock icon in the composer to schedule a post.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {/* Failed posts — shown prominently at top */}
              {failed.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-red-500/5">
                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">
                      Failed ({failed.length})
                    </span>
                  </div>
                  {failed.map(post => (
                    <ScheduledPostRow key={post.id} post={post} />
                  ))}
                </div>
              )}

              {/* Pending posts */}
              {pending.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted/30">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Pending ({pending.length})
                    </span>
                  </div>
                  {pending.map(post => (
                    <ScheduledPostRow key={post.id} post={post} />
                  ))}
                </div>
              )}

              {/* Published history */}
              {published.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted/30">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Published ({published.length})
                    </span>
                  </div>
                  {published.slice(0, 10).map(post => (
                    <ScheduledPostRow key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>


      </div>
    </div>
  );
}

function ScheduledPostRow({ post }: { post: ScheduledPost }) {
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Cancel this scheduled post?')) {
      cancelScheduledPost(post.id);
    }
  };

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    retryPost(post.id);
  };

  const handleReschedule = () => {
    if (!rescheduleDate || !rescheduleTime) return;
    const dt = new Date(`${rescheduleDate}T${rescheduleTime}`);
    if (isNaN(dt.getTime())) return;
    const ts = Math.floor(dt.getTime() / 1000);
    if (ts <= Math.floor(Date.now() / 1000) + 60) return;
    reschedulePost(post.id, ts);
    setShowReschedule(false);
  };

  const handlePresetReschedule = (ts: number) => {
    reschedulePost(post.id, ts);
    setShowReschedule(false);
  };

  // Detect attachment types for preview
  const imageAttachments = post.attachments?.filter(a => a.type === 'image') || [];
  const videoAttachments = post.attachments?.filter(a => a.type === 'video') || [];
  const audioAttachments = post.attachments?.filter(a => a.type === 'audio') || [];

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
          post.status === 'pending' ? 'bg-primary/10 text-primary' :
          post.status === 'published' ? 'bg-emerald-500/10 text-emerald-500' :
          'bg-red-500/10 text-red-500'
        }`}>
          {post.status === 'pending' ? <Clock className="w-4 h-4" /> :
           post.status === 'published' ? <CheckCircle className="w-4 h-4" /> :
           <AlertTriangle className="w-4 h-4" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-xs line-clamp-2 break-words">{post.content || '(media only)'}</p>

          {/* Attachment previews */}
          {(imageAttachments.length > 0 || videoAttachments.length > 0 || audioAttachments.length > 0) && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {imageAttachments.slice(0, 3).map((att, i) => (
                <img
                  key={i}
                  src={att.url}
                  alt=""
                  className="w-10 h-10 rounded object-cover border border-border"
                />
              ))}
              {imageAttachments.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{imageAttachments.length - 3}</span>
              )}
              {videoAttachments.length > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-[10px] text-muted-foreground">
                  <Film className="w-3 h-3" /> {videoAttachments.length} video{videoAttachments.length > 1 ? 's' : ''}
                </div>
              )}
              {audioAttachments.length > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-[10px] text-muted-foreground">
                  <Music className="w-3 h-3" /> {audioAttachments.length} audio
                </div>
              )}
            </div>
          )}

          {/* Time info & status */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] text-muted-foreground">
              {formatScheduledTime(post.scheduledAt)}
            </span>
            {post.status === 'pending' && (
              <span className="text-[10px] text-primary font-medium">
                in {timeUntil(post.scheduledAt)}
              </span>
            )}
            {post.status === 'published' && (
              <span className="text-[10px] text-emerald-500 font-medium flex items-center gap-0.5">
                <CheckCircle className="w-2.5 h-2.5" /> Published
              </span>
            )}
            {post.status === 'failed' && (
              <span className="text-[10px] text-red-500 font-medium">
                Failed{post.error ? `: ${post.error}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {post.status === 'pending' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setShowReschedule(!showReschedule); }}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                title="Reschedule"
              >
                <CalendarClock className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                title="Cancel"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {post.status === 'failed' && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
              title="Retry now"
            >
              <RotateCw className="w-3 h-3" /> Retry
            </button>
          )}
        </div>
      </div>

      {/* Reschedule panel */}
      {showReschedule && post.status === 'pending' && (
        <div className="ml-11 p-2.5 rounded-lg border border-border bg-muted/30 space-y-2">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Reschedule</p>
          {/* Quick presets */}
          <div className="flex flex-wrap gap-1">
            {getSchedulePresets().map(preset => (
              <button
                key={preset.label}
                onClick={() => handlePresetReschedule(preset.getTimestamp())}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
          {/* Custom date/time */}
          <div className="flex gap-1.5">
            <input
              type="date"
              value={rescheduleDate}
              onChange={e => setRescheduleDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="flex-1 bg-background rounded px-2 py-1 text-[11px] border border-border focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <input
              type="time"
              value={rescheduleTime}
              onChange={e => setRescheduleTime(e.target.value)}
              className="flex-1 bg-background rounded px-2 py-1 text-[11px] border border-border focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleReschedule}
              disabled={!rescheduleDate || !rescheduleTime}
              className="px-2.5 py-1 rounded text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Set
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BCHLogo({ size = 32 }: { size?: number }) {
   return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className="shrink-0">
      <circle cx="32" cy="32" r="32" fill="#0AC18E" />
      <path d="M41.5 27.2c.8-3.2-1.9-4.9-5.2-6l1.1-4.3-2.6-.6-1 4.2c-.7-.2-1.4-.3-2.1-.5l1-4.2-2.6-.6-1.1 4.3c-.6-.1-1.1-.3-1.6-.4l0 0-3.6-.9-.7 2.8s1.9.4 1.9.5c1.1.3 1.3 1 1.2 1.5l-1.2 5c.1 0 .2 0 .3.1l-.3-.1-1.7 6.9c-.1.3-.4.8-1.1.6 0 0-1.9-.5-1.9-.5l-1.3 3 3.4.8c.6.2 1.2.3 1.9.5l-1.1 4.4 2.6.6 1.1-4.3c.7.2 1.4.4 2.1.5l-1.1 4.3 2.6.6 1.1-4.4c4.5.9 7.9.5 9.3-3.6 1.1-3.3-.1-5.2-2.4-6.4 1.7-.4 3-1.6 3.3-4zm-6 8.3c-.8 3.3-6.3 1.5-8.1 1.1l1.4-5.8c1.8.4 7.5 1.3 6.7 4.7zm.8-8.4c-.7 3-5.3 1.5-6.8 1.1l1.3-5.2c1.5.4 6.3 1.1 5.5 4.1z" fill="white" />
    </svg>
  );
}

// ── Radio Mini-Player ─────────────────────────────────────────────
// Persistent bar shown above the bottom nav when radio is playing.
// Lets users control playback while browsing any page.
// Enhanced with like and tip actions for social music experience.

function RadioMiniPlayer() {
  const radio = useRadioPlayer();
  const location = useLocation();
  const viewportBottom = useViewportBottom();

  // Don't show mini-player on the radio page itself (full player is there)
  const onRadioPage = location.pathname === '/radio';

  // Only show if there's a track loaded (playing or paused)
  if (!radio.currentTrack || onRadioPage) return null;

  const progressPct = radio.duration > 0 ? (radio.currentTime / radio.duration) * 100 : 0;
  const likeCount = radio.likeCounts.get(radio.currentTrack.id) || 0;

  return (
    <div
      className="fixed left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border lg:hidden"
      style={{
        bottom: 'calc(3.5rem + env(safe-area-inset-bottom, 0px))',
        // Stays stacked on the nav as the visual viewport changes.
        transform: viewportBottom ? `translateY(-${viewportBottom}px)` : undefined,
      }}
    >
      {/* Progress bar — thin line at top */}
      <div className="h-0.5 w-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="flex items-center gap-2.5 px-3 py-2">
        {/* Cover art / disc */}
        <Link to={`/radio?track=${radio.currentTrack.id}`} className="shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 border border-border flex items-center justify-center overflow-hidden">
            {radio.currentTrack.coverArt ? (
              <img src={radio.currentTrack.coverArt} alt="" className="w-full h-full object-cover" />
            ) : (
              <Disc3 className={`w-5 h-5 text-primary/50 ${radio.isPlaying ? 'animate-spin' : ''}`} style={radio.isPlaying ? { animationDuration: '3s' } : undefined} />
            )}
          </div>
        </Link>

        {/* Track info */}
        <Link to={`/radio?track=${radio.currentTrack.id}`} className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">{radio.currentTrack.title}</p>
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] text-muted-foreground truncate">{radio.currentTrack.artist}</p>
            {likeCount > 0 && (
              <span className="flex items-center gap-0.5 text-[9px] text-red-500">
                <Heart className="w-2.5 h-2.5" /> {likeCount}
              </span>
            )}
          </div>
        </Link>

        {/* Controls */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Play/Pause */}
          <button
            onClick={radio.togglePlay}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all"
          >
            {radio.loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : radio.isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>

          {/* Skip next */}
          <button
            onClick={radio.skipNext}
            disabled={radio.currentIndex >= radio.queue.length - 1}
            className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <SkipForward className="w-4 h-4" />
          </button>

          {/* Close / stop */}
          <button
            onClick={radio.stop}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            title="Stop radio"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Desktop mini-player — shown at the bottom of the screen on large screens.
// Enhanced with like count, soundtrack badge, and tip button.
function RadioMiniPlayerDesktop() {
  const radio = useRadioPlayer();
  const location = useLocation();
  if (!radio.currentTrack || location.pathname === '/radio') return null;

  const progressPct = radio.duration > 0 ? (radio.currentTime / radio.duration) * 100 : 0;
  const likeCount = radio.likeCounts.get(radio.currentTrack.id) || 0;

  return (
    <div data-desktop-radio className="hidden lg:block fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border">
      {/* Progress */}
      <div className="h-0.5 w-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="flex items-center gap-3 px-4 py-2 max-w-screen-xl mx-auto">
        {/* Cover */}
        <Link to={`/radio?track=${radio.currentTrack.id}`} className="shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 border border-border flex items-center justify-center overflow-hidden">
            {radio.currentTrack.coverArt ? (
              <img src={radio.currentTrack.coverArt} alt="" className="w-full h-full object-cover" />
            ) : (
              <Disc3 className={`w-5 h-5 text-primary/50 ${radio.isPlaying ? 'animate-spin' : ''}`} style={radio.isPlaying ? { animationDuration: '3s' } : undefined} />
            )}
          </div>
        </Link>

        {/* Info */}
        <Link to={`/radio?track=${radio.currentTrack.id}`} className="min-w-0 w-48">
          <p className="text-xs font-semibold truncate">{radio.currentTrack.title}</p>
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] text-muted-foreground truncate">{radio.currentTrack.artist}</p>
            {likeCount > 0 && (
              <span className="flex items-center gap-0.5 text-[9px] text-red-500">
                <Heart className="w-2.5 h-2.5" /> {likeCount}
              </span>
            )}
          </div>
        </Link>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={radio.togglePlay}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all"
          >
            {radio.loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : radio.isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>
          <button onClick={radio.skipNext} disabled={radio.currentIndex >= radio.queue.length - 1} className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30">
            <SkipForward className="w-4 h-4" />
          </button>
        </div>

        {/* Time */}
        <span className="text-[10px] text-muted-foreground font-mono shrink-0">
          {formatDuration(radio.currentTime)} / {formatDuration(radio.duration)}
        </span>

        {/* Soundtrack mode badge */}
        {radio.soundtrackMode && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium bg-primary/10 text-primary border border-primary/20">
            <Music className="w-2.5 h-2.5" /> Soundtrack
          </span>
        )}

        {/* Tip artist */}
        <div className="shrink-0">
          <BCHTipButton recipientPubkey={radio.currentTrack.submittedBy} eventId={radio.currentTrack.id} />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Close */}
        <button onClick={radio.stop} className="p-1.5 text-muted-foreground hover:text-foreground" title="Stop radio">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

